'use client'

import { useCallback, useEffect, useState } from 'react'
import {
  BANNER_SHOW_EVENT,
  PREFS_CHANGE_EVENT,
  type ChannelActivityPref,
  type ChannelPref,
  type NotificationPrefs,
  defaultPrefs,
  dismissBanner,
  getNotificationPrefs,
  isBannerDismissed,
  resetBannerDismissed,
  setBadgeSource,
  setBrowserEnabled,
  setChannelActivityPref,
  setContentLevel,
  setKindChannel,
  setSoundEnabled,
  setSystemWhenFocused,
  type BadgeSource,
  type ContentLevel,
} from '@/lib/notifications/prefs'
import type { NotificationKind } from '@/services/notifications/types'

export interface UseNotificationPrefsResult {
  prefs: NotificationPrefs
  bannerDismissed: boolean
  setSoundEnabled: (value: boolean) => void
  setBrowserEnabled: (value: boolean) => void
  setKindChannel: (kind: NotificationKind, channel: ChannelPref, value: boolean) => void
  setChannelActivityPref: (key: ChannelActivityPref, value: boolean) => void
  setContentLevel: (value: ContentLevel) => void
  setSystemWhenFocused: (value: boolean) => void
  setBadgeSource: (value: BadgeSource) => void
  dismissBanner: () => void
  resetBannerDismissed: () => void
}

/**
 * Vista reativa das prefs. Sincroniza entre componentes (CustomEvent) e entre
 * abas (evento `storage`) — o storage é a fonte da verdade, o state é só cache
 * de render.
 */
export function useNotificationPrefs(): UseNotificationPrefsResult {
  // Começa no default e hidrata no effect: o storage não existe no SSR e ler
  // durante o render daria mismatch de hidratação.
  const [prefs, setPrefs] = useState<NotificationPrefs>(defaultPrefs)
  const [bannerDismissed, setBannerDismissed] = useState(false)

  useEffect(() => {
    const sync = () => {
      setPrefs(getNotificationPrefs())
      setBannerDismissed(isBannerDismissed())
    }
    sync()
    window.addEventListener(PREFS_CHANGE_EVENT, sync)
    window.addEventListener(BANNER_SHOW_EVENT, sync)
    window.addEventListener('storage', sync)
    return () => {
      window.removeEventListener(PREFS_CHANGE_EVENT, sync)
      window.removeEventListener(BANNER_SHOW_EVENT, sync)
      window.removeEventListener('storage', sync)
    }
  }, [])

  return {
    prefs,
    bannerDismissed,
    // Os setters só escrevem: quem atualiza o state é o listener acima, então
    // toda instância do hook converge no mesmo valor.
    setSoundEnabled: useCallback((value: boolean) => setSoundEnabled(value), []),
    setBrowserEnabled: useCallback((value: boolean) => setBrowserEnabled(value), []),
    setKindChannel: useCallback(
      (kind: NotificationKind, channel: ChannelPref, value: boolean) =>
        setKindChannel(kind, channel, value),
      [],
    ),
    setChannelActivityPref: useCallback(
      (key: ChannelActivityPref, value: boolean) => setChannelActivityPref(key, value),
      [],
    ),
    setContentLevel: useCallback((value: ContentLevel) => setContentLevel(value), []),
    setSystemWhenFocused: useCallback((value: boolean) => setSystemWhenFocused(value), []),
    setBadgeSource: useCallback((value: BadgeSource) => setBadgeSource(value), []),
    dismissBanner: useCallback(() => dismissBanner(), []),
    resetBannerDismissed: useCallback(() => resetBannerDismissed(), []),
  }
}
